import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { withCountRu } from '@dorixona/utils'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { Icon } from '../../ui/Icon'
import { Pagination } from '../../ui/Pagination'
import { DetailHead } from '../../ui/DetailHead'
import { Failed, Loading, failureText } from '../../ui/Load'
import { useSession } from '../auth/session'
import { useStaffPage } from '../admins/api'
import { useStores, type Store } from '../stores/api'
import { storeTitleById } from '../stores/model'
import { useRoles, useSaveRole, type Role } from './api'
import {
  PERMISSION_LETTERS,
  moduleName,
  openModules,
  rolePermission,
  setAllPermissions,
  togglePermission,
} from './model'

export interface RoleScreenProps {
  onToast: (message: string) => void
}

/** Сотрудников роли — по 10 на странице. */
const STAFF_SIZES = [10, 25, 50] as const

/** Что видит и что меняет роль — по разделу в строке. */
export function RoleScreen({ onToast }: RoleScreenProps) {
  const navigate = useNavigate()
  const params = useParams()
  const roles = useRoles()
  const stores = useStores()

  if (roles.isPending) return <Loading what="роль" />
  if (roles.isError) {
    return (
      <Failed
        what="роль"
        error={roles.error}
        onRetry={() => {
          void roles.refetch()
        }}
      />
    )
  }

  const role = roles.data.items.find((r) => r.id === Number(params.id))
  if (!role) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-key" />
          <b>Роль не найдена</b>
          <p>Возможно, её удалили.</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/roles')
            }}
          >
            К списку ролей
          </button>
        </div>
      </div>
    )
  }

  // Ключ — id роли: другая роль начинается с чистого черновика.
  return (
    <RoleCard
      key={role.id}
      role={role}
      modules={roles.data.modules}
      stores={stores.data ?? []}
      onToast={onToast}
    />
  )
}

interface RoleCardProps {
  role: Role
  modules: string[]
  stores: Store[]
  onToast: (message: string) => void
}

/** Есть ли в черновике права, которых нет на сервере. */
function changed(draft: Role, role: Role, modules: string[]): boolean {
  return modules.some((module) => rolePermission(draft, module) !== rolePermission(role, module))
}

/**
 * Карточка загруженной роли. Сетка прав — это форма: щелчки по флажкам и
 * «Открыть всё» / «Снять всё» копятся в черновике и уходят одной кнопкой
 * «Сохранить» одним запросом, а не запросом на каждый флажок.
 */
function RoleCard({ role, modules, stores, onToast }: RoleCardProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const save = useSaveRole()
  // null — правок нет, сетка показывает то, что на сервере.
  const [edits, setEdits] = useState<Role | null>(null)

  const draft = edits ?? role
  const dirty = edits !== null && changed(edits, role, modules)

  // Уход со страницы с несохранёнными правами браузер переспросит.
  useUnsavedWarning(dirty)

  const canUpdate = can('roles', 'u')
  const editable = canUpdate && !role.system
  // Сотрудники роли — страницами с сервера: считать их по полному списку
  // значило бы тянуть весь штат ради одного числа.
  const [staffPage, setStaffPage] = useState(1)
  const [staffSize, setStaffSize] = useState<number>(STAFF_SIZES[0])
  const staff = useStaffPage({ roleId: role.id, page: staffPage, pageSize: staffSize })
  const mine = staff.data?.items ?? []
  const open = openModules(role, modules)

  function saveAll() {
    save.mutate(
      { id: role.id, name: role.name, perms: draft.perms },
      {
        onSuccess: () => {
          setEdits(null)
          onToast(`Права роли «${role.name}» сохранены`)
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  return (
    <>
      <DetailHead
        backLabel="Роли доступа"
        onBack={() => {
          void navigate('/roles')
        }}
        title={role.name}
        sub={
          <>
            {role.system ? <span className="ui-tag">системная роль</span> : null}
            <span className="num">
              {withCountRu(role.staffCount, ['сотрудник', 'сотрудника', 'сотрудников'])}
            </span>
            <span className="dot" aria-hidden="true" />
            <span className="num">
              {open.length} из {modules.length} разделов открыто
            </span>
          </>
        }
        actions={canUpdate ? null : <span className="ui-tag">только просмотр</span>}
      />

      <div className="ui-detail-grid">
        <section className="ui-table-card" aria-labelledby="rl-perms">
          <div className="ui-card-head">
            <h3 id="rl-perms">Доступ по разделам</h3>
            {role.system ? (
              <span className="aside">
                <Icon name="i-key" />
                права зашиты в системе
              </span>
            ) : (
              <span className="aside">
                <Icon name="i-info" />
                любое право включает чтение
              </span>
            )}
            {editable ? (
              <span className="rl-bulk">
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => {
                    setEdits(setAllPermissions(draft, modules, true))
                  }}
                >
                  Открыть всё
                </button>
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => {
                    setEdits(setAllPermissions(draft, modules, false))
                  }}
                >
                  Снять всё
                </button>
              </span>
            ) : null}
          </div>
          <div className="ui-table-wrap">
            <table className="ui-table rl-perms">
              <colgroup>
                <col />
                {PERMISSION_LETTERS.map(([letter]) => (
                  <col key={letter} className="w-letter" />
                ))}
                <col className="w-sum" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Раздел</th>
                  {PERMISSION_LETTERS.map(([letter, title]) => (
                    <th scope="col" key={letter} data-align="center">
                      {title}
                      <span className="rl-key" aria-hidden="true">
                        {letter.toUpperCase()}
                      </span>
                    </th>
                  ))}
                  <th scope="col" data-align="end">
                    Итог
                  </th>
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => {
                  const perm = rolePermission(draft, module)
                  const name = moduleName(module)
                  return (
                    <tr key={module}>
                      <td className="primary">{name}</td>
                      {PERMISSION_LETTERS.map(([letter, title]) => (
                        <td key={letter} data-align="center">
                          <label className="ui-check rl-cell">
                            <input
                              type="checkbox"
                              aria-label={`${name}: ${title}`}
                              checked={perm.includes(letter)}
                              disabled={!editable}
                              onChange={() => {
                                setEdits(togglePermission(draft, module, letter))
                              }}
                            />
                          </label>
                        </td>
                      ))}
                      <td data-align="end">
                        {perm ? (
                          <span className="ui-tag rl-sum">{perm.toUpperCase()}</span>
                        ) : (
                          <span className="secondary">нет доступа</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="ui-card" aria-labelledby="rl-staff">
          <div className="ui-card-head">
            <h3 id="rl-staff">
              Сотрудники
              {staff.data ? <span className="ui-count">{staff.data.total}</span> : null}
            </h3>
            <span className="aside">назначаются в «Сотрудниках»</span>
          </div>
          <div className="ui-card-body">
            {staff.isPending ? <p className="ui-muted-line">Загружаем сотрудников…</p> : null}
            {staff.isError ? (
              <Failed
                what="сотрудников"
                error={staff.error}
                onRetry={() => {
                  void staff.refetch()
                }}
              />
            ) : null}
            {staff.data?.items.length === 0 ? (
              <p className="ui-muted-line">Роль пока никому не назначена</p>
            ) : null}
            {mine.length > 0 ? (
              <ul className="rl-people">
                {mine.map((person) => (
                  <li key={person.id}>
                    <span className="ui-person">
                      <span className="ui-avatar" aria-hidden="true">
                        {person.name.slice(0, 1).toUpperCase()}
                      </span>
                      <span className="txt">
                        <b>{person.name}</b>
                        <span className="rl-where">
                          {person.storeId
                            ? storeTitleById(stores, person.storeId, 'аптека')
                            : 'все аптеки'}
                        </span>
                      </span>
                    </span>
                    <span className="ui-status" data-tone={person.active ? 'success' : undefined}>
                      {person.active ? 'активен' : 'отключён'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {staff.data && staff.data.total > STAFF_SIZES[0] ? (
            <Pagination
              label="Страницы сотрудников роли"
              page={staffPage}
              pageSize={staffSize}
              total={staff.data.total}
              sizes={STAFF_SIZES}
              busy={staff.isPlaceholderData}
              onPage={setStaffPage}
              onPageSize={(size) => {
                setStaffSize(size)
                setStaffPage(1)
              }}
            />
          ) : null}
        </section>
      </div>

      {editable ? (
        <div className="ui-savebar" data-dirty={dirty || undefined}>
          <span className="state" role="status">
            {dirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены'}
          </span>
          {dirty ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                setEdits(null)
              }}
            >
              Отменить
            </button>
          ) : null}
          <button
            type="button"
            className="ui-btn"
            disabled={!dirty || save.isPending}
            aria-busy={save.isPending}
            onClick={saveAll}
          >
            Сохранить
          </button>
        </div>
      ) : null}
    </>
  )
}
